import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  analizarConversacionCopiloto,
  getConversacionStats,
  guardarInteraccionCopiloto,
  listarInteraccionesCopiloto,
} from '../../../services/copilotoComercialService';
import ConversacionPegadaEditor from './ConversacionPegadaEditor';
import ResultadoCopiloto from './ResultadoCopiloto';
import RespuestasSugeridasCopiloto from './RespuestasSugeridasCopiloto';
import HistorialCopiloto from './HistorialCopiloto';

const getApiErrorMessage = (err) =>
  err.data?.mensaje || err.data?.message || err.message || 'No fue posible procesar la accion.';

export default function CopilotoComercialPanel({
  prospecto,
  proyectos = [],
  desarrollos = [],
  puedeUsar = false,
}) {
  const [form, setForm] = useState(() => ({
    canal: 'WHATSAPP',
    campanaOrigen: prospecto?.origenDetalle || '',
    proyectoId: prospecto?.proyectoId || '',
    desarrolloId: prospecto?.desarrolloId || '',
    nombreProspecto: prospecto?.nombre || '',
  }));
  const [mensajes, setMensajes] = useState([]);
  const [analisis, setAnalisis] = useState(null);
  const [respuestaEnviada, setRespuestaEnviada] = useState('');
  const [historial, setHistorial] = useState({ items: [], pagina: 1, tamanoPagina: 20, totalPaginas: 1, totalRegistros: 0 });
  const [analizando, setAnalizando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);
  const [error, setError] = useState('');
  const [errorHistorial, setErrorHistorial] = useState('');
  const [mensaje, setMensaje] = useState('');

  const stats = useMemo(() => getConversacionStats(mensajes), [mensajes]);
  const prospectoId = prospecto?.id || prospecto?.prospectoId || '';

  useEffect(() => {
    setForm((actual) => ({
      ...actual,
      campanaOrigen: actual.campanaOrigen || prospecto?.origenDetalle || '',
      proyectoId: actual.proyectoId || prospecto?.proyectoId || '',
      desarrolloId: actual.desarrolloId || prospecto?.desarrolloId || '',
      nombreProspecto: prospecto?.nombre || '',
    }));
  }, [prospecto]);

  const cargarHistorial = useCallback(async ({ pagina = 1, append = false } = {}) => {
    if (!prospectoId) return;
    setCargandoHistorial(true);
    setErrorHistorial('');

    try {
      const data = await listarInteraccionesCopiloto(prospectoId, { pagina, tamanoPagina: 20 });
      setHistorial((actual) => ({
        ...data,
        items: append ? [...actual.items, ...data.items] : data.items,
      }));
    } catch (err) {
      setErrorHistorial(getApiErrorMessage(err));
    } finally {
      setCargandoHistorial(false);
    }
  }, [prospectoId]);

  useEffect(() => {
    cargarHistorial();
  }, [cargarHistorial]);

  const analizar = async () => {
    setError('');
    setMensaje('');

    if (!prospectoId) {
      setError('Primero guarda o selecciona un prospecto para usar el Copiloto.');
      return;
    }

    if (!mensajes.length) {
      setError('Agrega al menos un mensaje para analizar.');
      return;
    }

    if (stats.excedeMensajes || stats.excedeCaracteresPorMensaje) {
      setError('La conversacion excede los limites permitidos.');
      return;
    }

    setAnalizando(true);
    try {
      const data = await analizarConversacionCopiloto({
        prospectoId,
        canal: form.canal,
        nombreProspecto: form.nombreProspecto,
        campanaOrigen: form.campanaOrigen,
        proyectoId: form.proyectoId,
        desarrolloId: form.desarrolloId,
        conversacion: mensajes,
      });
      setAnalisis(data);
      setRespuestaEnviada(data.respuestasSugeridas[0]?.texto || '');
      setMensaje('Analisis generado correctamente.');
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setAnalizando(false);
    }
  };

  const guardar = async () => {
    setError('');
    setMensaje('');

    if (!prospectoId) {
      setError('Primero guarda o selecciona un prospecto para registrar esta interaccion.');
      return;
    }

    if (!analisis) {
      setError('Analiza una conversacion antes de guardar.');
      return;
    }

    setGuardando(true);
    try {
      await guardarInteraccionCopiloto({
        prospectoId,
        canal: form.canal,
        campanaOrigen: form.campanaOrigen,
        proyectoId: form.proyectoId,
        desarrolloId: form.desarrolloId,
        resumen: analisis.resumen,
        etapaComercial: analisis.etapaComercial,
        temperatura: analisis.temperatura,
        formaPago: analisis.formaPago,
        intencionPrincipal: analisis.intencionPrincipal,
        objeciones: analisis.objecionesDetectadas,
        etiquetas: analisis.etiquetasSugeridas,
        proximaAccionTipo: analisis.proximaAccion?.tipo,
        proximaAccionFecha: analisis.proximaAccion?.fechaSugerida,
        proximaAccionPrioridad: analisis.proximaAccion?.prioridad,
        respuestaEnviada,
      });
      setMensaje('Analisis guardado en CRM.');
      await cargarHistorial();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setGuardando(false);
    }
  };

  if (!puedeUsar) {
    return (
      <section className="crm-prospectos-card">
        <p className="crm-prospectos-feedback is-error">No tienes permiso para usar el Copiloto Comercial.</p>
      </section>
    );
  }

  return (
    <section className="copiloto-panel">
      <div className="crm-prospectos-card copiloto-intro">
        <div>
          <p className="crm-prospectos-eyebrow">Copiloto Comercial</p>
          <h2>Apoyo para seguimiento comercial</h2>
          <p>Analiza conversaciones pegadas manualmente y registra el resumen en el historial del prospecto.</p>
          <small>Revisa la sugerencia antes de enviarla al prospecto.</small>
        </div>
        <button type="button" className="is-primary" onClick={guardar} disabled={guardando || !analisis || !prospectoId}>
          {guardando ? 'Guardando...' : 'Guardar analisis en CRM'}
        </button>
      </div>

      {!prospectoId ? <p className="crm-prospectos-feedback is-error">Primero guarda o selecciona un prospecto para registrar esta interaccion.</p> : null}
      {mensaje ? <p className="crm-prospectos-feedback is-ok">{mensaje}</p> : null}
      {error ? <p className="crm-prospectos-feedback is-error">{error}</p> : null}

      <ConversacionPegadaEditor
        form={form}
        mensajes={mensajes}
        proyectos={proyectos}
        desarrollos={desarrollos}
        onFormChange={setForm}
        onMensajesChange={setMensajes}
        disabled={analizando}
        onAnalyze={analizar}
      />
      <ResultadoCopiloto analisis={analisis} onChange={setAnalisis} />
      <RespuestasSugeridasCopiloto
        respuestas={analisis?.respuestasSugeridas || []}
        respuestaEnviada={respuestaEnviada}
        onRespuestaChange={setRespuestaEnviada}
        onCopied={setMensaje}
      />
      <HistorialCopiloto
        historial={historial}
        cargando={cargandoHistorial}
        error={errorHistorial}
        onRetry={() => cargarHistorial()}
        onLoadMore={() => cargarHistorial({ pagina: Number(historial.pagina || 1) + 1, append: true })}
      />
    </section>
  );
}
